import { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Copy, CreditCard, Download, FileUp, Loader2, MessageCircle, Package } from "lucide-react";
import { API, api, brl, errMsg, fmtDate, img, STATUS } from "@/lib/api";
import { useFetch } from "@/hooks/useFetch";
import { Eyebrow } from "@/components/site/Motion";

const StatusBadge = ({ status }) => (
  <span className={`inline-flex rounded-full border px-4 py-1.5 text-sm font-semibold ${STATUS[status]?.tone || ""}`} data-testid="order-status-badge">{STATUS[status]?.label || status}</span>
);

const pixCopy = (key) => {
  const digits = key.replace(/\D/g, "");
  return /^[\d\s()+-]+$/.test(key) && /^55\d{10,11}$/.test(digits) ? `+${digits}` : key.trim();
};

const PixBox = ({ order, token, reload }) => {
  const s = order.settings || {};
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/orders/${order.id}/pix-proof?t=${token}`, fd);
      toast.success("Comprovante enviado! Assim que confirmarmos, o download é liberado.");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const wa = s.whatsapp ? `https://wa.me/${s.whatsapp.replace(/\D/g, "")}?text=${encodeURIComponent(`Olá! Segue o comprovante do pedido ${order.id.slice(-6)} (${brl(order.amount_cents)}).`)}` : null;

  return (
    <div className="rounded-[1.5rem] border border-white/10 bg-ink-2 p-6 sm:p-8" data-testid="pix-box">
      <p className="font-mono text-xs uppercase tracking-[0.2em] text-flash">Pagamento via Pix</p>
      <p className="mt-3 font-display text-4xl font-bold">{brl(order.amount_cents)}</p>
      {s.pix_key ? (
        <div className="mt-6 space-y-3">
          <p className="text-sm text-haze">Chave Pix {s.pix_holder && <>· favorecido <b className="text-foreground">{s.pix_holder}</b></>}</p>
          <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-ink p-2 pl-4">
            <code className="flex-1 break-all font-mono text-sm" data-testid="pix-key">{s.pix_key}</code>
            <button onClick={() => { navigator.clipboard?.writeText(pixCopy(s.pix_key)); toast.success("Chave copiada"); }} className="rounded-lg bg-white/10 p-2.5 hover:bg-white/20" data-testid="pix-copy-btn" aria-label="Copiar"><Copy className="h-4 w-4" /></button>
          </div>
        </div>
      ) : (
        <p className="mt-4 text-sm text-amber-300" data-testid="pix-missing">A chave Pix ainda não foi configurada. Fale com a gente pelo WhatsApp.</p>
      )}
      <div className="mt-6 flex flex-wrap gap-3">
        <input ref={fileRef} type="file" accept="image/*,application/pdf" hidden onChange={(e) => upload(e.target.files?.[0])} data-testid="pix-proof-input" />
        <button disabled={busy} onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2 rounded-full bg-flash px-6 py-3 font-semibold text-ink hover:bg-flash-2 disabled:opacity-60" data-testid="pix-proof-upload-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />} {order.has_proof ? "Reenviar comprovante" : "Enviar comprovante"}
        </button>
        {wa && <a href={wa} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 font-semibold hover:border-flash" data-testid="pix-whatsapp-btn"><MessageCircle className="h-4 w-4" /> Enviar pelo WhatsApp</a>}
      </div>
    </div>
  );
};

const MpBox = ({ order, token }) => {
  const [busy, setBusy] = useState(false);
  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/${order.id}/pay?t=${token}`);
      window.location.assign(data.init_point);
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };
  return (
    <div className="rounded-[1.5rem] border border-white/10 bg-ink-2 p-6 sm:p-8" data-testid="mp-box">
      <p className="font-mono text-xs uppercase tracking-[0.2em] text-flash">Mercado Pago</p>
      <p className="mt-3 font-display text-4xl font-bold">{brl(order.amount_cents)}</p>
      <p className="mt-3 text-sm text-haze">Estamos aguardando a confirmação do Mercado Pago. Esta página atualiza sozinha.</p>
      <button onClick={pay} disabled={busy} className="mt-6 inline-flex items-center gap-2 rounded-full bg-flash px-6 py-3 font-semibold text-ink disabled:opacity-60" data-testid="mp-pay-btn">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />} Pagar com Mercado Pago
      </button>
    </div>
  );
};

export default function OrderPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const token = params.get("t") || "";
  const { data: order, loading, error, reload } = useFetch(`/orders/${id}?t=${token}`);

  useEffect(() => {
    if (!order || order.status === "approved" || order.status === "rejected") return undefined;
    const t = setInterval(reload, order.method === "mercadopago" ? 6000 : 15000);
    return () => clearInterval(t);
  }, [order, reload]);

  if (loading) return <main className="min-h-screen pt-40 text-center text-haze">Carregando pedido…</main>;
  if (error || !order) return <main className="min-h-screen pt-40 text-center" data-testid="order-not-found"><p className="font-display text-3xl font-bold">Pedido não encontrado.</p><p className="mt-2 text-haze">Confira se o link está completo.</p></main>;

  const approved = order.status === "approved";
  const photos = Array.isArray(order.photos) ? order.photos : [];
  const dl = (pid) => `${API}/orders/${order.id}/download/${pid}?t=${token}`;

  return (
    <main className="mx-auto max-w-[1200px] px-5 pb-24 pt-[130px] sm:px-8" data-testid="order-page">
      <Eyebrow>Pedido #{order.id.slice(-6).toUpperCase()}</Eyebrow>
      <div className="mt-5 flex flex-wrap items-center gap-4">
        <h1 className="font-display text-4xl font-extrabold tracking-tight sm:text-6xl">{approved ? "Suas fotos estão prontas." : order.kind === "booking" ? "Reserva de data" : "Quase lá."}</h1>
        <StatusBadge status={order.status} />
      </div>
      <p className="mt-4 text-haze">{order.description}{order.event && <> · <Link to={`/eventos/${order.event.slug}`} className="text-flash">ver galeria</Link></>}</p>
      <p className="mt-1 text-sm text-haze">Guarde este link: é por ele que você acessa seu pedido.</p>

      {order.kind === "booking" && order.booking && (
        <div className="mt-8 grid gap-3 rounded-[1.5rem] border border-white/10 bg-ink-2 p-6 sm:grid-cols-3" data-testid="booking-details">
          <div><p className="text-xs text-haze">Fotógrafo</p><p className="font-semibold">{order.photographer_name}</p></div>
          <div><p className="text-xs text-haze">Data</p><p className="font-semibold">{fmtDate(order.booking.date)}</p></div>
          <div><p className="text-xs text-haze">Evento</p><p className="font-semibold">{order.booking.event_type}</p></div>
        </div>
      )}

      {!approved && order.status !== "rejected" && (
        <div className="mt-8 grid gap-6 lg:grid-cols-2">
          {order.method === "pix" ? <PixBox order={order} token={token} reload={reload} /> : <MpBox order={order} token={token} />}
          <div className="rounded-[1.5rem] border border-white/10 p-6 sm:p-8">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-haze">O que acontece agora</p>
            <ol className="mt-4 space-y-3 text-foreground/90">
              <li>1. Faça o pagamento de <b>{brl(order.amount_cents)}</b>.</li>
              <li>2. {order.method === "pix" ? "Envie o comprovante aqui ou pelo WhatsApp." : "O Mercado Pago confirma automaticamente."}</li>
              <li>3. {order.kind === "booking" ? "Sua data fica reservada com o fotógrafo." : "Os downloads em alta são liberados nesta página."}</li>
            </ol>
            {order.status === "awaiting_confirmation" && <p className="mt-5 rounded-xl bg-violet/15 p-4 text-sm text-violet-lilac" data-testid="order-awaiting-msg">Recebemos seu comprovante. A confirmação costuma ser rápida.</p>}
          </div>
        </div>
      )}

      {order.status === "rejected" && <p className="mt-8 rounded-xl bg-red-500/10 p-5 text-red-300" data-testid="order-rejected-msg">Este pagamento não foi aprovado. Fale com a gente ou faça um novo pedido.</p>}

      {approved && order.kind === "booking" && (
        <p className="mt-8 flex items-center gap-3 rounded-xl bg-emerald-500/10 p-5 text-emerald-300" data-testid="booking-confirmed-msg"><CheckCircle2 className="h-5 w-5" /> Pagamento confirmado — sua data está reservada. O fotógrafo vai entrar em contato.</p>
      )}

      {photos.length > 0 && (
        <section className="mt-12">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <h2 className="font-display text-2xl font-bold">{photos.length} foto(s) no pedido</h2>
            {approved && <a href={`${API}/orders/${order.id}/zip?t=${token}`} className="inline-flex items-center gap-2 rounded-full bg-flash px-6 py-3 font-semibold text-ink hover:bg-flash-2" data-testid="order-download-zip-btn"><Package className="h-4 w-4" /> Baixar todas (.zip)</a>}
          </div>
          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {photos.map((p) => (
              <div key={p.id} className="overflow-hidden rounded-2xl border border-white/10 bg-ink-2">
                <img src={img("thumb", p.id)} alt={p.code} className="aspect-[4/3] w-full object-cover" />
                <div className="flex items-center justify-between p-3">
                  <span className="font-mono text-xs">{p.code}</span>
                  {approved ? (
                    <a href={dl(p.id)} className="inline-flex items-center gap-1 text-sm font-semibold text-flash" data-testid={`order-download-${p.code}`}><Download className="h-4 w-4" /> Baixar</a>
                  ) : <span className="text-xs text-haze">bloqueada</span>}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </main>
  );
}
